/**
 * Location Edit - Single photo module
 *
 * This module can be used to display and update a single photo. It allows for
 * photos to be added via the asset selector for accounts with digital asset
 * management and otherwise falls back to a basic uploader.
 *
 * @author MattJS (matt@yext.com)
 */
goog.provide('yext.location.photo');

goog.require('yext');
goog.require('yext.Field');
goog.require('yext.dam.assetselector');
goog.require('yext.dam.assetselector.events');
goog.require('yext.dam.assetselector.usage');
goog.require('yext.dam.labels.view');
goog.require('yext.dialog');
goog.require('yext.gmb');
goog.require('yext.image');
goog.require('yext.image.Constraints');
goog.require('yext.location.helpers');
goog.require('yext.locationcluster');
goog.require('yext.locationcluster.ClusterGuard');

goog.scope(function() {
  var _photo = yext.location.photo;

  var errorMessage = yext.msg('There was an error saving your change');

  /**
   * SinglePhoto class
   *
   * Represents a single photo field
   *
   * @constructor
   *
   * @param {!jQuery} $markup The single photo container
   * @param {!Object} options Options can be provided as an object
   */
  var SinglePhoto = function($markup, options) {
    this.$markup = $markup;
    this.configuration = this.$markup.data();
    this.$addPhoto = this.$markup.find('.js-add-single-photo');
    this.$photoWrap = this.$markup.find('.js-single-photo-wrap');
    this.$photoActions = this.$markup.find('.js-photo-actions');
    this.$variousMessage = this.$markup.find('.js-various-content-message');
    this.$photoDialog = this.$markup.find('.js-single-photo-dialog');

    // Type safety
    if (typeof options != 'object') {
      options = {};
    }
    this.options = options;

    if (this.configuration) {
      this.hasGMBSubscription = this.configuration['hasGmbSubscription'];
      this.fieldId = this.configuration['fieldId'];
      this.fieldName = this.configuration['fieldName'];
    }

    // Stores data about the currently selected photo
    this.data = {};
    // The markup will be a form in the edit case
    this.isEdit = this.$markup.is('form');

    this.getCurrentPhoto();
    this.createField();
    this.bindEvents();
    this.bindDialog();
    this.renderConstraintLabel();
  };

  /**
   * Get the current photo if there is one
   */
  SinglePhoto.prototype.getCurrentPhoto = function() {
    var $photo = this.$markup.find('.js-current-photo');
    if ($photo.length && $photo.is(':visible')) {
      this.data = {
        url: $photo.data('url'),
      };
    }
  };

  /**
   * Create the field object and listen for some events
   */
  SinglePhoto.prototype.createField = function() {
    if (this.isEdit) {
      var self = this;
      var timeout;

      // Field class makes the form submit via ajax
      this.field = new yext.Field(this.$markup);
      // Headshot field
      if (yext.locationcluster.shouldShowWarningDialog(this.fieldName)) {
        new yext.locationcluster.ClusterGuard(this.field);
      }

      this.field.$form.on('beforeSubmit.field', function() {
        timeout = yext.dialog.showDelayedProgressDialog(yext.msg('Updating...'));
      });

      this.field.$form.on('save.field', function(e, response) {
        if (response.stale) {
          if (self.options.alertErrors) {
            yext.dialog.showErrorDialog(yext.location.helpers.staleErrorMessage);
          } else {
            yext.location.helpers.showStaleError(self.$markup);
          }
        } else {
          yext.location.helpers.hideError(self.$markup);
        }

        if (response.field) {
          self.$markup.closest('tr')
            .toggleClass('overridable-mode', response.field.isInherited === true);
          if (response.field.url) {
            self.data = {
              url: response.field.url,
            };
          }
          self.$markup.find('.js-timestamp').val(response.field.timestamp);
          self.renderPhoto(response.field);
        } else {
          self.error();
        }
      });

      this.field.$form.on('error.field', function() {
        self.error();
      });

      this.field.$form.on('always.field', function() {
        yext.dialog.hideDelayedProgressDialog(timeout);
      });
    }
  };

  /**
   * Show generic error
   */
  SinglePhoto.prototype.error = function() {
    if (this.options.alertErrors) {
      yext.dialog.showErrorDialog(errorMessage);
    } else {
      yext.location.helpers.showError(errorMessage, this.$markup);
    }
  };

  /**
   * Bind events for replace and remove
   * @suppress {deprecated}
   */
  SinglePhoto.prototype.bindEvents = function() {
    var self = this;
    var removeDialog;

    // Bind add photo
    this.$markup.find('.js-add-photo').click(function() {
      var constraints = new yext.image.Constraints({
        'container': $(this),
      });
      var type = 'PHOTO';
      if (self.options.uploadOnly) {
        yext.dam.assetselector.showLoaderOnly(type);
      } else {
        const usageType = goog.module.get('yext.dam.assetselector.usage')
          .forProfileFields(self.fieldId);
        yext.dam.assetselector.show({
          'type': type,
          'includeUsedAssets': false,
          'usageType': usageType,
        },
        self.fieldName,
        undefined, // custom field id
        constraints.get());
      }
      self.bindAssetSelector();
    });

    // Bind remove photo dialog
    if (this.isEdit) {
      var $dialog = this.$markup.find('.js-rm-photo-dialog');

      removeDialog = $dialog.dialog({
        modal: true,
        draggable: false,
        resizable: false,
        autoOpen: false,
        title: yext.msg('Are you sure you want to remove this photo?'),
        width: 355,
        height: 100,
      });

      $dialog.find('.danger').click(function() {
        self.remove();
        removeDialog.dialog('close');
      });

      $dialog.find('.cancel').click(function() {
        removeDialog.dialog('close');
      });
    }

    // Bind remove photo
    this.$markup.find('.js-remove-photo').click(function() {
      if (self.isEdit) {
        removeDialog.dialog('open');
      } else {
        self.clear(true);
      }
    });
  };

  /**
   * Show larger format of photo in dialog
   */
  SinglePhoto.prototype.bindDialog = function() {
    bindDialog(this);
  };

  /**
   * Bind asset selector add event
   */
  SinglePhoto.prototype.bindAssetSelector = function() {
    var self = this;

    // Message from asset selector when a photo is chosen
    $(yext.dam.assetselector.events).on('added.assetselector', function(e, asset) {
      self.data = {
        url: asset.photoUrl,
        file: asset.file,
        fileInput: asset.fileInput,
        croppedImage: asset.croppedImage,
      };
      if (self.isEdit) {
        self.upload();
      } else {
        self.addImage();
      }
    });
  };

  /**
   * Upload the new photo
   */
  SinglePhoto.prototype.upload = function() {
    var fileName;

    if (this.data.file) {
      fileName = this.data.file.name;
    } else {
      fileName = this.data.url;
    }

    if (this.data.croppedImage) {
      this.field.additionalData = [{
        name: this.fieldName + '.croppedImage',
        value: this.data.croppedImage,
      },
      {
        name: this.fieldName + '.croppedImageName',
        value: fileName,
      }];
    } else if (this.data.file) {
      this.field.addFile(this.data.file, this.data.fileInput);

      this.field.additionalData = [{
        name: this.fieldName + '.index',
        value: 0,
      }];
    } else {
      this.field.additionalData = [{
        name: this.fieldName + '.url',
        value: this.data.url,
      }];
    }
    this.field.$form.submit();
  };

  /**
   * Remove the business logo
   */
  SinglePhoto.prototype.remove = function() {
    this.field.additionalData = [{
      name: 'delete',
      value: true,
    }];

    this.field.$form.submit();
  };


  /**
   * This function checks whether the location has Google My Business subscription and
   * if so, checks if the image matches the correct dimensions for google
   */
  SinglePhoto.prototype.renderConstraintLabel = function() {
    var self = this;

    if (this.data.url) {
      yext.image.getImageDimensions(this.data.url, function(dimensions) {
        if (self.hasGMBSubscription
            && !yext.image.areValidDimensions(yext.gmb.PROFILE_PHOTO_CONSTRAINTS, dimensions)) {
          var $constraintLabel = yext.dam.labels.view.getAssetViewLabel(null, true);
          if ($constraintLabel) {
            self.$markup.find('.js-single-photo-wrap').append($constraintLabel);
          }
        }
      });
    }
  };

  /**
   * Render the business logo
   * @param {!Object} field Description of logo field
   */
  SinglePhoto.prototype.renderPhoto = function(field) {
    if (!field.indeterminate && field.url) {
      this.addImage();
    } else {
      this.clear(!field.isInherited);
    }
    this.$variousMessage.toggle(field.indeterminate);
    this.$markup.find('.js-remove-photo-wrapper').toggle(!field.isInherited);
    this.renderConstraintLabel();
  };

  /**
   * Clear the markup for when the logo is removed
   * @param {boolean} showAddPhoto True if add photo dialog should be shown
   */
  SinglePhoto.prototype.clear = function(showAddPhoto) {
    if (showAddPhoto) {
      this.$addPhoto.show();
      this.$photoActions.hide();
      this.$variousMessage.hide();
    }
    this.$photoWrap.hide().empty();

    this.data = {};
  };

  /**
   * Add a new image
   */
  SinglePhoto.prototype.addImage = function() {
    var self = this;
    var img = new Image();

    img.onload = function() {
      self.addImagePresentation();

      // Optionally size the photo to fit inside a container if the size option
      // is provided
      if (self.options.size) {
        yext.sizeImage(this, self.options.size.width, self.options.size.height);
      }

      self.$photoWrap.empty().append(this).show();
    };

    // If there are issues loading the images
    img.onerror = function() {
      self.addImagePresentation();

      yext.location.helpers.fallbackImage(function(img) {
        self.$photoWrap.empty().append(img).show();
      });
    };

    if (this.data.croppedImage) {
      img.src = this.data.croppedImage;
    } else {
      img.src = this.data.url;
    }
  };

  /**
   * Presentation changes for addImage
   */
  SinglePhoto.prototype.addImagePresentation = function() {
    this.$addPhoto.hide();
    this.$photoActions.show();
    this.$variousMessage.hide();
  };

  /**
   * View only version of single photo
   * @constructor
   */
  var SinglePhotoView = function($markup) {
    this.$markup = $markup;
    this.$photoWrap = this.$markup.find('.js-single-photo-wrap');
    this.$photoDialog = this.$markup.find('.js-single-photo-dialog');

    this.data = {};
    this.data.url = this.$markup.find('.js-current-photo').data('url');

    this.bindDialog();
  };

  /**
   * Show larger format of photo in dialog
   */
  SinglePhotoView.prototype.bindDialog = function() {
    bindDialog(this);
  };

  /**
   * Clean up to prevent memory leaks
   */
  SinglePhotoView.prototype.destroy = function() {
    this.$photoWrap.off('.singlePhotoView');
    this.$photoDialog.dialog('destroy');
    if (this.img) {
      this.img.onload = function() {};
    }
  };

  /**
   * Create new SinglePhoto instance
   *
   * @param {!jQuery} $markup The single photo container
   * @param {!Object} options Options can be provided as an object
   */
  _photo.create = function($markup, options) {
    return new SinglePhoto($markup, options);
  };

  /**
   * Create new SinglePhotoView instance
   *
   * @param {!jQuery} $markup The single photo container
   */
  _photo.createView = function($markup) {
    return new SinglePhotoView($markup);
  };

  /**
   * Bind dialog is shared between SinglePhoto and SinglePhotoView
   */
  var bindDialog = function(self) {
    if (self.$photoDialog.length) {
      var title = self.$photoDialog.data('title');
      if (typeof title != 'string' || !title) {
        title = yext.msg('Photo');
      }

      self.$photoWrap.on('click.singlePhotoView', function() {
        self.img = new Image();

        self.img.onload = function() {
          self.$photoDialog.empty().append(this);

          self.$photoDialog.dialog({
            modal: true,
            draggable: false,
            resizable: false,
            autoOpen: true,
            title: title,
            width: 'auto',
            height: 'auto',
          });
        };
        self.img.src = self.data.url;
      });
    }
  };
});
